/**
 * Подписи дат заметок — в часовом поясе человека. Общие для сайта и приложения.
 *
 * Напоминание назначают «на 18:00» по своим часам, и показывать его
 * в поясе сервера значило бы спорить с часами на стене.
 */

const TIME = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const DAY_MONTH = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" });
const DAY_MONTH_LONG = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });
const FULL = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Дата изменения в списке: сегодня — время, в этом году — «6 окт.», раньше — полностью. */
export function editedLabel(iso: string, now: Date = new Date()): string {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return "";
  if (sameDay(moment, now)) return TIME.format(moment);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(moment, yesterday)) return "вчера";
  if (moment.getFullYear() === now.getFullYear()) return DAY_MONTH.format(moment);
  return FULL.format(moment);
}

/** Момент напоминания: «сегодня в 18:00», «завтра в 9:00», «10 октября в 18:00». */
export function reminderLabel(iso: string, now: Date = new Date()): string {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return "";
  const time = TIME.format(moment);
  if (sameDay(moment, now)) return `сегодня в ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (sameDay(moment, tomorrow)) return `завтра в ${time}`;
  const date = moment.getFullYear() === now.getFullYear() ? DAY_MONTH_LONG.format(moment) : FULL.format(moment);
  return `${date} в ${time}`;
}

/** Короткая подпись у пункта: «18:00», «завтра 9:00», «10 окт. 18:00». */
export function reminderShortLabel(iso: string, now: Date = new Date()): string {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return "";
  const time = TIME.format(moment);
  if (sameDay(moment, now)) return time;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (sameDay(moment, tomorrow)) return `завтра ${time}`;
  return `${DAY_MONTH.format(moment)} ${time}`;
}

/** «Изменено 14:05» или «Изменено 6 окт.» — для шапки заметки. */
export function savedAtLabel(iso: string, now: Date = new Date()): string {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return "";
  if (sameDay(moment, now)) return `сегодня в ${TIME.format(moment)}`;
  return `${editedLabel(iso, now)} в ${TIME.format(moment)}`;
}

/** Локальные дата (YYYY-MM-DD) и время (HH:MM) момента — для полей ввода. */
export function splitLocal(iso: string): { date: string; time: string } {
  const moment = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, "0");
  return {
    date: `${String(moment.getFullYear())}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}`,
    time: `${pad(moment.getHours())}:${pad(moment.getMinutes())}`,
  };
}

/** Момент из локальных даты и времени — ISO со смещением пояса человека. */
export function joinLocal(date: string, time: string): string | null {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  if ([year, month, day, hours, minutes].some((part) => part === undefined || Number.isNaN(part))) return null;
  const moment = new Date(year ?? 0, (month ?? 1) - 1, day ?? 1, hours ?? 0, minutes ?? 0, 0, 0);
  return Number.isNaN(moment.getTime()) ? null : moment.toISOString();
}
